import React, { FC, useEffect, useState } from "react"
import type { BlackoutDate } from "../../Api/BlackoutDateApi"

interface EditBlackoutModalProps {
  isOpen: boolean
  date: Date | null
  blackout: BlackoutDate | null
  onClose: () => void
  onSave: (data: { date: string; reason: string; is_active: boolean }) => Promise<void>
  onDelete: (id: number) => Promise<void>
}

const EditBlackoutModal: FC<EditBlackoutModalProps> = ({
  isOpen,
  date,
  blackout,
  onClose,
  onSave,
  onDelete
}) => {
  const [reason, setReason] = useState('')
  const [isActive, setIsActive] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [reasonError, setReasonError] = useState('')
  const [saveError, setSaveError] = useState('')
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  
  useEffect(() => {
    if (isOpen && blackout) {
      // モーダルが開かれたときに既存データで初期化
      setReason(blackout.reason || '')
      setIsActive(blackout.is_active)
      setIsSaving(false) // isSavingもリセット
      setReasonError('')
      setSaveError('')
      setShowDeleteConfirm(false)
    }
  }, [isOpen, blackout])
  
  const formatDateForDisplay = (date: Date): string => {
    const year = date.getFullYear()
    const month = date.getMonth() + 1
    const day = date.getDate()
    const weekdays = ['日', '月', '火', '水', '木', '金', '土']
    const weekday = weekdays[date.getDay()]
    
    return `${year}年${month}月${day}日(${weekday})`
  }
  
  const formatDateForKey = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }
  
  const handleSave = async () => {
    if (!date) return
    
    // バリデーション
    const trimmedReason = reason.trim()
    if (!trimmedReason) {
      setReasonError('理由は必須です。')
      return
    }
    
    setReasonError('')
    setSaveError('')
    
    try {
      setIsSaving(true)
      await onSave({
        date: formatDateForKey(date),
        reason: trimmedReason,
        is_active: isActive
      })
      // 成功時もisSavingをリセット（モーダルが閉じられる前に）
      setIsSaving(false)
      // onSave内でモーダルを閉じる処理が行われるので、ここでは何もしない
    } catch (error) {
      console.error('保存エラー:', error)
      setSaveError(error instanceof Error ? error.message : '保存に失敗しました。')
      setIsSaving(false)
    }
  }
  
  const handleDeleteClick = () => {
    setShowDeleteConfirm(true)
  }
  
  const handleDeleteConfirm = async () => {
    if (!blackout) return
    
    try {
      await onDelete(blackout.id)
      setShowDeleteConfirm(false)
      onClose()
    } catch (error) {
      console.error('削除エラー:', error)
      setSaveError(error instanceof Error ? error.message : '削除に失敗しました。')
      setShowDeleteConfirm(false)
    }
  }
  
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false)
  }
  
  if (!isOpen || !date || !blackout) return null
  
  return (
    <div className={`fixed inset-0 z-[9999] ${isOpen ? 'flex' : 'hidden'} items-center justify-center`}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-xl shadow-lg max-w-md w-[90%] max-h-[90vh] overflow-y-auto animate-[modalSlideIn_0.3s_ease]">
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900 m-0">予約不可日を編集</h3>
          <button
            className="bg-transparent border-none cursor-pointer text-gray-400 hover:text-gray-900 transition-colors p-1"
            onClick={onClose}
          >
            <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
        <div className="p-6">
          <div className="mb-6">
            <label htmlFor="modalDate" className="block font-semibold text-gray-900 mb-2">日付</label>
            <input
              type="text"
              id="modalDate"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-gray-50 text-gray-600"
              value={formatDateForDisplay(date)}
              readOnly
            />
          </div>
          <div className="mb-6">
            <label htmlFor="modalReason" className="block font-semibold text-gray-900 mb-2">理由 <span className="text-red-500">*</span></label>
            <textarea
              id="modalReason"
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-y ${
                reasonError 
                  ? 'border-red-500 focus:ring-red-500 focus:border-red-500' 
                  : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
              }`}
              rows={3}
              placeholder="例：会社休業日、メンテナンス、年末年始など"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value)
                if (reasonError) {
                  setReasonError('')
                }
              }}
            />
            {reasonError && (
              <p className="mt-1 text-sm text-red-500">{reasonError}</p>
            )}
          </div>
          {saveError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{saveError}</p>
            </div>
          )}
          <div>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                id="modalIsActive"
                className="w-[18px] h-[18px] cursor-pointer"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span className="text-sm text-gray-900">アクティブ（予約を受け付けない）</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
          <button
            className="inline-flex items-center justify-center px-5 py-2.5 bg-gray-100 text-gray-900 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
            onClick={onClose}
          >
            キャンセル
          </button>
          <button
            className="inline-flex items-center justify-center px-5 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-all"
            onClick={handleDeleteClick}
          >
            削除
          </button>
          <button
            className="inline-flex items-center justify-center px-5 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
      
      {/* 削除確認モーダル */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDeleteCancel}></div>
          <div className="relative bg-white rounded-xl shadow-lg max-w-md w-[90%] p-6 animate-[modalSlideIn_0.3s_ease]">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">削除の確認</h3>
            <p className="text-gray-700 mb-6">この予約不可日を削除しますか？</p>
            <div className="flex justify-end gap-3">
              <button
                className="inline-flex items-center justify-center px-5 py-2.5 bg-gray-100 text-gray-900 border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
                onClick={handleDeleteCancel}
              >
                キャンセル
              </button>
              <button
                className="inline-flex items-center justify-center px-5 py-2.5 bg-red-500 text-white rounded-lg text-sm font-medium hover:bg-red-600 transition-all"
                onClick={handleDeleteConfirm}
              >
                削除する
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EditBlackoutModal

